
import { GeneratedBlogContent, FAQItem } from "../types";
import { GoogleGenAI } from "@google/genai";

async function callProxy(endpoint: string, payload: any) {
  try {
    const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`AI Request Failed: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error(`Gemini Service Error:`, error);
    throw error;
  }
}

export const callAiAssistant = async (prompt: string) => {
  return await callProxy('chat-proxy', { message: prompt });
};

export const createChatSession = () => {
  return {
    sendMessageStream: async ({ message }: { message: string }) => {
      const data = await callProxy('chat-proxy', { message });
      return (async function* () {
        yield { text: data.text, sources: data.sources };
      })();
    }
  };
};

/**
 * 直接使用 GoogleGenAI 生成圖像
 */
export const generateImageFromPrompt = async (prompt: string): Promise<string | null> => {
  try {
    const apiKey = (import.meta as any).env?.VITE_GEMINI_API_KEY || (process.env.API_KEY);
    if (!apiKey) throw new Error("No API Key found for image generation.");

    const ai = new GoogleGenAI({ apiKey });
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: { parts: [{ text: prompt }] },
      config: {
        imageConfig: { aspectRatio: "16:9" }
      }
    });

    for (const part of response.candidates?.[0]?.content?.parts || []) {
      if (part.inlineData) {
        return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
      }
    }
    return null;
  } catch (error) {
    console.error("Image Generation Error:", error);
    return null;
  }
};

export const generateBlogPostContent = async (topic: string): Promise<GeneratedBlogContent | null> => {
  try {
    const data = await callProxy('admin-journal-proxy', { message: topic });
    const rawText = data.text || "";
    
    const excerptRegex = /\[EXCERPT\](.*?)(?=\[CATEGORY\]|\[CONTENT\]|\[SEO_TAGS\]|\[IMAGE_PLAN\]|$)/si;
    const categoryRegex = /\[CATEGORY\](.*?)(?=\[CONTENT\]|\[SEO_TAGS\]|\[IMAGE_PLAN\]|$)/si;
    const contentRegex = /\[CONTENT\](.*?)(?=\[SEO_TAGS\]|\[IMAGE_PLAN\]|$)/si;
    const tagsRegex = /\[SEO_TAGS\](.*?)(?=\[IMAGE_PLAN\]|$)/si;
    const imagePlanRegex = /\[IMAGE_PLAN\](.*?)$/si;

    const excerpt = rawText.match(excerptRegex)?.[1].trim() || "";
    const category = rawText.match(categoryRegex)?.[1].trim() || "Nutrition";
    const content = rawText.match(contentRegex)?.[1].trim() || rawText;
    const tagsRaw = rawText.match(tagsRegex)?.[1].trim() || "";
    const imagePlan = rawText.match(imagePlanRegex)?.[1].trim() || "";

    // 解析主圖與多組插圖
    const mainImagePromptMatch = imagePlan.match(/Main Image Prompt\s*:\s*(.*?)(?=Illustration|$)/si);
    const mainImagePrompt = mainImagePromptMatch?.[1].trim() || "Natural pet photography, minimalist style.";

    const illustrationPrompts: string[] = [];
    const illustrationMatches = imagePlan.matchAll(/Illustration \d+ Prompt\s*:\s*(.*?)(?=Illustration|$)/gsi);
    for (const match of illustrationMatches) {
      if (match[1]) illustrationPrompts.push(match[1].trim());
    }

    const tags = tagsRaw.split(/[,\s]+/)
      .map(t => t.trim().replace(/[*.\s]/g, '')) 
      .filter(t => t.length > 1)
      .map(t => t.startsWith('#') ? t : `#${t}`);
    
    return { 
      title: topic,
      category,
      content, 
      excerpt, 
      imagePrompt: mainImagePrompt,
      illustrationPrompts,
      tags,
      faqs: [] // FAQs handled separately if needed
    };
  } catch (error) {
    return null;
  }
};
